import { useMutation } from '@tanstack/react-query';
import { gamificationServiceApi, type ApiResponse } from '@/services/api';

// Badge interface matching new backend response
export interface Badge {
  badgeName: string;
  description: string;
  iconUrl: string;
  rewardPoints: number;
  rewardGems: number;
}

// Level info from backend
export interface LevelInfo {
  previous_level: number;
  previous_level_xp: number;
  previous_next_level_xp: number;
  level: number;
  current_level_xp: number;
  next_level_xp: number;
  level_up: boolean;
  received_gems: number | null;
  received_xp: number | null;
}

// New backend response structure
export interface AwardBadgesResponse {
  newBadges: Badge[];
  count: number;
  level_info: LevelInfo | null;
}

// Backend envelope: { success, message, data }
interface BackendEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

export const useAwardBadges = () => {
  return useMutation<AwardBadgesResponse, Error, void>({
    mutationFn: async () => {
      const res: ApiResponse<BackendEnvelope<AwardBadgesResponse>> = await gamificationServiceApi.get<BackendEnvelope<AwardBadgesResponse>>(
        '/user-badges/award'
      );
      // res.data is the backend envelope, unwrap its .data
      if (res.success && res.data && res.data.data) {
        return {
          ...res.data.data,
          count: res.data.data.newBadges?.length || 0,
        };
      }
      throw new Error(res.error || 'Failed to get badges');
    },
    retry: false,
  });
};
